import React from 'react';
import { ChevronDown, Search } from 'lucide-react';

/**
 * ONE SET OF APP PRIMITIVES FOR EVERY DASHBOARD SCREEN.
 *
 * Every colour here is a SEMANTIC TOKEN from index.css (--app-*), never a
 * literal hex. That is the whole point of the file: light mode is the
 * industry-standard neutral product stack (near-white canvas, white
 * surfaces, 1px neutral hairlines, near-black text) and the brand accent is
 * spent only on primary actions, focus and active state.
 *
 * Cream and espresso stay on the marketing pages. In dense product UI a
 * cream panel on a cream ground gives no value separation, which is what
 * made these screens read as beige and dated.
 *
 * DARK MODE, LATER: remap the --app-* variables under one
 * `[data-theme='dark']` block in index.css. Do not add a second palette to
 * these components, and do not reintroduce hardcoded hex — a component that
 * names a colour instead of a role is the one that will break the theme.
 */

/** Roles, for the rare case a token is needed in an inline style. */
export const APP_TOKEN = {
  canvas: 'var(--app-canvas)',
  surface: 'var(--app-surface)',
  welcome: 'var(--app-welcome)',
  border: 'var(--app-border)',
  text: 'var(--app-text)',
  textMuted: 'var(--app-text-muted)',
  accent: 'var(--app-accent)'
} as const;

/** Resting surface: separation comes from tone and one restrained shadow. */
export const SURFACE = 'rounded-xl bg-app-surface shadow-[0_1px_2px_rgba(20,20,24,0.05)]';

/**
 * Title, one line of explanation, one action. The content below is the subject,
 * so the header stays compact while remaining comfortably readable.
 */
export function ScreenHeader({
  title,
  description,
  action
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return <div className="flex flex-wrap items-start gap-4" data-id="element-2992" data-mp="3uvz5c9m5kiur">
      <div className="mr-auto min-w-0" data-id="element-2993" data-mp="25jorir7gav91">
        <h1 className="text-[21px] font-semibold tracking-[-0.015em] text-app" data-id="element-2994" data-mp="25kqbxmacwu3f">{title}</h1>
        {description && <p className="mt-1.5 max-w-[68ch] text-[14.5px] leading-6 text-app-muted" data-id="element-2995" data-mp="2q7y29n92ig67">
            {description}
          </p>}
      </div>
      {action}
    </div>;
}

/** The one filled control on a screen: the brand accent, used once. */
export function PrimaryAction({
  children,
  label
}: {
  children?: React.ReactNode;
  label?: string;
}) {
  return <button type="button" aria-label={label} className="inline-flex h-9 flex-none items-center gap-1.5 rounded-lg bg-app-accent px-3.5 text-[13.5px] font-semibold text-white transition-colors duration-200 ease-out hover:bg-[var(--app-accent-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent)] focus-visible:ring-offset-2" data-id="element-2996" data-mp="348ij4dvfa1x2">
      {children}
    </button>;
}

/** A quiet bordered control. Neutral at rest, accent border on focus. */
export function SelectPill({
  value,
  onChange,
  options,
  label,
  prefix
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  label: string;
  prefix?: string;
}) {
  return <div className="relative" data-id="element-2997" data-mp="1l9b5qv6li7dn">
      <select value={value} onChange={event => onChange(event.target.value)} aria-label={label} className="h-9 appearance-none rounded-lg border border-app bg-app-surface pl-3 pr-8 text-[13.5px] font-medium text-app outline-none transition-colors duration-200 ease-out hover:border-app-strong focus:border-app-accent" data-id="element-2998" data-mp="1lacjj7ezx7z2">
        {options.map(option => <option key={option} value={option} data-id="element-2999" data-mp="23z9d192kjwg8">
            {prefix ? `${prefix} · ${option}` : option}
          </option>)}
      </select>
      <ChevronDown size={13} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-app-subtle" data-id="element-3000" data-mp="1z1r2je8wl964" />
    </div>;
}

/** Layout / view switcher. The active segment is the text colour, not a hue. */
export function Segmented<T extends string>({
  value,
  onChange,
  options
}: {
  value: T;
  onChange: (value: T) => void;
  options: readonly (readonly [T, React.ElementType])[];
}) {
  return <div className="flex h-9 flex-none items-center gap-0.5 rounded-lg bg-app-subtle p-1" data-id="element-3001" data-mp="2hxqyt18wyyvi">
      {options.map(([key, Icon]) => <button key={key} type="button" onClick={() => onChange(key)} aria-pressed={value === key} aria-label={`${key} layout`} className={`rounded-md p-1.5 transition-colors duration-200 ease-out ${value === key ? 'bg-app-surface text-app shadow-[0_1px_2px_rgba(20,20,24,0.08)]' : 'text-app-subtle hover:text-app'}`} data-id="element-3002" data-mp="22jdiw9nxzvfx">
          <Icon size={14} data-id="element-3003" data-mp="2l7s4ghfhqh9c" />
        </button>)}
    </div>;
}
export function SearchField({
  value,
  onChange,
  placeholder,
  label
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
}) {
  return <div className="relative" data-id="element-3004" data-mp="2bgxm3o5d81t6">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-app-subtle" data-id="element-3005" data-mp="2bhp6s3xfcf4o" />
      <input type="search" value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} aria-label={label} className="h-10 w-full rounded-lg border border-app bg-app-surface pl-9 pr-3 text-[14px] text-app outline-none transition-colors duration-200 ease-out placeholder:text-app-subtle focus:border-app-accent" data-id="element-3006" data-mp="2gf8vjxrr0aav" />
    </div>;
}

/**
 * A GROUPED LIST, not a grid of boxes. One surface, hairline dividers: a list
 * has one left edge to run your eye down, where a grid draws a rectangle
 * around every item and gives them all identical weight.
 */
export function Panel({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`overflow-hidden ${SURFACE} ${className}`} data-id="element-3007" data-mp="ek1j6kbj0byd">{children}</div>;
}

/** One row inside a Panel. */
export function Row({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`group flex items-center gap-3.5 border-b border-app-divider px-4 py-3.5 transition-colors duration-200 ease-out last:border-0 hover:bg-app-hover ${className}`} data-id="element-3008" data-mp="jimb7wgmjpbq">
      {children}
    </div>;
}

/**
 * The row's mark. Quiet by default, accented only while the row is pointed
 * at — an always-on tinted tile per row turns a list into a wall of badges.
 */
export function Glyph({
  icon: Icon
}: {
  icon: React.ElementType;
}) {
  return <span aria-hidden="true" className="flex size-8 flex-none items-center justify-center rounded-lg bg-app-subtle text-app-muted transition-colors duration-200 ease-out group-hover:text-app" data-id="element-3009" data-mp="wknikm21rud2">
      <Icon size={15} strokeWidth={1.9} data-id="element-3010" data-mp="15llmz5gee3w6" />
    </span>;
}

/** Row title over one line of supporting detail. */
export function RowText({
  title,
  meta
}: {
  title: string;
  meta: React.ReactNode;
}) {
  return <span className="min-w-0 flex-1" data-id="element-3011" data-mp="dw8r5xntiebq">
      <span className="block truncate text-[14.5px] font-medium text-app" data-id="element-3012" data-mp="dt37ixxyk4ne">{title}</span>
      <span className="mt-0.5 block truncate text-[12.5px] leading-5 text-app-muted" data-id="element-3013" data-mp="huy5bsgojzwf">{meta}</span>
    </span>;
}

/**
 * Status as a small dot, on the semantic status tokens rather than the brand
 * accent — "active" is a state, not a call to action, and colour-coding it
 * orange competed with the one control that should own the accent.
 */
export function StatusRing({
  status
}: {
  status: 'active' | 'dormant' | 'archived';
}) {
  const tone = status === 'active' ? 'bg-[var(--app-success)]' : status === 'dormant' ? 'bg-[var(--app-text-subtle)]' : 'bg-[var(--app-border-strong)]';
  return <span aria-label={status} title={status} className={`size-[7px] flex-none rounded-full ${tone}`} data-id="element-3014" data-mp="39uriq8bfe9x0" />;
}

/** A number over its label. Number first — it is what is being compared. */
export function MetaStat({
  label,
  value
}: {
  label: string;
  value: React.ReactNode;
}) {
  return <div data-id="element-3015" data-mp="2v27fl7oa02fk">
      <dd className="text-[16px] font-semibold leading-none tabular-nums text-app" data-id="element-3016" data-mp="2v1nzlwi6ro46">{value}</dd>
      <dt className="mt-1.5 text-[12px] text-app-subtle" data-id="element-3017" data-mp="2q4xjxaaamuja">{label}</dt>
    </div>;
}

/** The empty state. */
export function EmptyState({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className={`${SURFACE} px-6 py-14 text-center text-[13.5px] text-app-muted`} data-id="element-3018" data-mp="2h3g9o8jkhn2i">{children}</div>;
}